"use client";

import React, { useState, useRef } from "react";
import { UploadCloud, FileText, CheckCircle2, X, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export interface UploadedFileItem {
  name: string;
  size: number;
  type: string;
  base64?: string;
}

interface FileUploadProps {
  onFileSelect?: (file: UploadedFileItem | null) => void;
  acceptedFormats?: string[];
  maxSizeMb?: number;
  label?: string;
  hint?: string;
  className?: string;
}

export function FileUpload({
  onFileSelect,
  acceptedFormats = [".pdf", ".docx", ".zip", ".png", ".jpg", ".csv"],
  maxSizeMb = 15,
  label = "Attach Project Specs / RFP / Wireframes",
  hint = "PDF, DOCX, ZIP, or PNG/JPG up to 15MB",
  className = "",
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<UploadedFileItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSimulatingUpload, setIsSimulatingUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const processFile = (file: File) => {
    setError(null);

    // Validate size
    if (file.size > maxSizeMb * 1024 * 1024) {
      setError(`File size exceeds maximum allowed ${maxSizeMb}MB.`);
      return;
    }

    // Validate extension
    const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
    const isValid = acceptedFormats.some((fmt) => fmt.toLowerCase() === extension);
    if (!isValid) {
      setError(`Invalid file format. Accepted: ${acceptedFormats.join(", ")}`);
      return;
    }

    // Simulate upload progress
    setIsSimulatingUpload(true);
    setUploadProgress(15);

    const timer1 = setTimeout(() => setUploadProgress(65), 150);
    const timer2 = setTimeout(() => {
      setUploadProgress(100);
      setIsSimulatingUpload(false);

      const reader = new FileReader();
      reader.onload = (e) => {
        const item: UploadedFileItem = {
          name: file.name,
          size: file.size,
          type: file.type || extension,
          base64: e.target?.result as string,
        };
        setUploadedFile(item);
        onFileSelect?.(item);
      };
      reader.readAsDataURL(file);
    }, 380);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    setError(null);
    setUploadProgress(0);
    if (inputRef.current) inputRef.current.value = "";
    onFileSelect?.(null);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
          {label}
        </label>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={acceptedFormats.join(",")}
        onChange={handleFileInput}
        className="hidden"
      />

      {!uploadedFile ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all duration-200 ${
            isDragging
              ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20 scale-[0.99]"
              : "border-slate-300 dark:border-slate-700 hover:border-blue-400 dark:hover:border-cyan-500 bg-slate-50/50 dark:bg-slate-900/30"
          }`}
        >
          <div className="flex flex-col items-center justify-center gap-2">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform ${
                isDragging ? "scale-110 text-blue-600 bg-blue-100" : "text-slate-400 bg-slate-100 dark:bg-slate-800"
              }`}
            >
              <UploadCloud className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-800 dark:text-slate-200">
                <span className="text-blue-600 dark:text-cyan-400 font-semibold underline">
                  Click to upload
                </span>{" "}
                or drag & drop
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>
            </div>
          </div>

          {/* Upload Progress Simulation */}
          {isSimulatingUpload && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100 dark:bg-slate-800 overflow-hidden rounded-b-xl">
              <motion.div
                className="h-full bg-blue-600 dark:bg-cyan-400"
                style={{ width: `${uploadProgress}%` }}
                transition={{ duration: 0.2 }}
              />
            </div>
          )}
        </div>
      ) : (
        /* Uploaded File Card */
        <motion.div
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between p-3 rounded-xl border border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-950/20"
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                {uploadedFile.name}
              </p>
              <p className="text-[10px] text-slate-400 flex items-center gap-1">
                <span>{formatFileSize(uploadedFile.size)}</span>
                <span>•</span>
                <span className="text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-0.5">
                  <CheckCircle2 className="w-2.5 h-2.5" /> Attached
                </span>
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={removeFile}
            className="p-1 rounded-md text-slate-400 hover:text-rose-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            title="Remove attachment"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      )}

      {error && (
        <p className="text-[11px] text-rose-500 flex items-center gap-1 pt-0.5 font-medium">
          <AlertCircle className="w-3 h-3 shrink-0" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
